import { useRef } from "react";
import { X } from "lucide-react";
import type { CuratedModelTag } from "../data/curated-models";
import { useI18n } from "../hooks/use-i18n";
import { useTagRowOverflow } from "../hooks/use-tag-row-overflow";
import { localizeFacetValue } from "../lib/localization";
import { gsap, useGSAP } from "../lib/gsap";
import type {
  ReleaseFilterListKey,
  ReleaseFilters
} from "../lib/query-models";
import type { OrganizationIndex } from "../lib/organizations";

/**
 * The height the row takes in the header. The row pads itself out to keep its
 * chips' focus rings inside its scroll clip and gives the space back with a
 * negative margin, so its own box is taller than the room it occupies.
 */
function occupiedHeight(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  return (
    element.offsetHeight +
    parseFloat(style.marginTop) +
    parseFloat(style.marginBottom)
  );
}

interface ActiveFiltersProps {
  query: string;
  filters: ReleaseFilters;
  onClearQuery: () => void;
  onRemove: (key: ReleaseFilterListKey, value: string) => void;
  onClearDates: () => void;
  onClearAll: () => void;
  organizations: OrganizationIndex;
}

export function ActiveFilters({
  query,
  filters,
  onClearQuery,
  onRemove,
  onClearDates,
  onClearAll,
  organizations
}: ActiveFiltersProps) {
  const { language, t } = useI18n();
  const motionRef = useRef<HTMLDivElement>(null);
  const expandedHeight = useRef(0);
  const wasVisible = useRef(false);
  const chips = [
    ...filters.curatedTags.map((value) => ({
      key: "curatedTags" as const,
      value
    })),
    ...filters.companies.map((value) => ({ key: "companies" as const, value })),
    ...filters.categories.map((value) => ({ key: "categories" as const, value })),
    ...filters.capabilities.map((value) => ({
      key: "capabilities" as const,
      value
    })),
    ...filters.statuses.map((value) => ({ key: "statuses" as const, value })),
    ...filters.openness.map((value) => ({ key: "openness" as const, value }))
  ];
  const hasDates = filters.from || filters.to;
  const visible = Boolean(query || chips.length || hasDates);
  const visibleRef = useRef(visible);
  // The row scrolls sideways once its chips outgrow the header, and fades only
  // the edge that still hides a chip, like the tag rows on the cards. Its own
  // size does not change when a chip is added, so the chips are the key.
  const contentRef = useTagRowOverflow<HTMLDivElement>(
    [
      language,
      query,
      filters.from,
      filters.to,
      ...chips.map((chip) => `${chip.key}:${chip.value}`)
    ].join("\u0000")
  );

  useGSAP(
    () => {
      const region = motionRef.current;
      const content = contentRef.current;
      if (!region || !content) return;
      visibleRef.current = visible;

      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      const previouslyVisible = wasVisible.current;
      const targetHeight = occupiedHeight(content);
      gsap.killTweensOf([region, content]);

      if (reduced) {
        gsap.set(region, {
          height: visible ? "auto" : 0,
          overflow: visible ? "visible" : "hidden"
        });
        gsap.set(content, {
          autoAlpha: visible ? 1 : 0,
          y: 0,
          clearProps: visible ? "opacity,visibility,transform" : "transform"
        });
        if (visible) expandedHeight.current = targetHeight;
        wasVisible.current = visible;
        return;
      }

      if (visible) {
        expandedHeight.current = targetHeight;
        if (!previouslyVisible) {
          gsap.set(region, { height: 0, overflow: "hidden" });
          gsap.set(content, { autoAlpha: 0, y: -6 });
          gsap.to(content, {
            autoAlpha: 1,
            y: 0,
            duration: 0.2,
            ease: "power2.out",
            clearProps: "opacity,visibility,transform"
          });
        }

        const currentHeight = region.getBoundingClientRect().height;
        if (Math.abs(currentHeight - targetHeight) < 0.5) {
          gsap.set(region, { height: "auto", overflow: "visible" });
        } else {
          gsap.to(region, {
            height: targetHeight,
            duration: 0.26,
            ease: "power3.inOut",
            overwrite: "auto",
            onComplete: () => {
              gsap.set(region, { height: "auto", overflow: "visible" });
            }
          });
        }
      } else {
        const currentHeight = Math.max(
          region.getBoundingClientRect().height,
          expandedHeight.current
        );
        gsap.set(region, { height: currentHeight, overflow: "hidden" });
        gsap.to(content, {
          autoAlpha: 0,
          y: -4,
          duration: 0.14,
          ease: "power2.in",
          overwrite: "auto"
        });
        gsap.to(region, {
          height: 0,
          duration: 0.22,
          ease: "power3.inOut",
          overwrite: "auto",
          onComplete: () => {
            gsap.set(content, { y: 0 });
          }
        });
      }

      wasVisible.current = visible;
    },
    { scope: motionRef, dependencies: [visible] }
  );

  useGSAP(
    () => {
      const region = motionRef.current;
      const content = contentRef.current;
      if (!region || !content || !("ResizeObserver" in window)) return;

      const observer = new ResizeObserver(() => {
        if (!visibleRef.current) return;
        const targetHeight = occupiedHeight(content);
        if (Math.abs(targetHeight - expandedHeight.current) < 0.5) return;

        expandedHeight.current = targetHeight;
        if (
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
          gsap.set(region, { height: "auto", overflow: "visible" });
          return;
        }

        const currentHeight = region.getBoundingClientRect().height;
        gsap.killTweensOf(region);
        gsap.set(region, { height: currentHeight, overflow: "hidden" });
        gsap.to(region, {
          height: targetHeight,
          duration: 0.22,
          ease: "power3.inOut",
          overwrite: "auto",
          onComplete: () => {
            gsap.set(region, { height: "auto", overflow: "visible" });
          }
        });
      });

      observer.observe(content);
      return () => observer.disconnect();
    },
    { scope: motionRef }
  );

  return (
    <div
      ref={motionRef}
      className="active-filters-motion"
      aria-hidden={!visible}
      data-active={visible ? "true" : "false"}
    >
      <div
        ref={contentRef}
        className="active-filters"
        aria-label={visible ? t("activeFilters") : undefined}
        inert={!visible}
      >
        <span className="active-filters__label">{t("active")}</span>
        {query ? (
          <button type="button" className="filter-chip" onClick={onClearQuery}>
            <small>{t("search")}</small>
            <span>“{query}”</span>
            <X size={12} aria-hidden="true" />
          </button>
        ) : null}
        {chips.map((chip) => (
          <button
            type="button"
            className="filter-chip"
            key={`${chip.key}:${chip.value}`}
            onClick={() => onRemove(chip.key, chip.value)}
          >
            <span>
              {chip.key === "curatedTags"
                ? t(chip.value as CuratedModelTag)
                : localizeFacetValue(
                    chip.key,
                    chip.value,
                    language,
                    organizations
                  )}
            </span>
            <X size={12} aria-hidden="true" />
          </button>
        ))}
        {hasDates ? (
          <button type="button" className="filter-chip" onClick={onClearDates}>
            <small>{t("date")}</small>
            <span>
              {filters.from || t("any")} → {filters.to || t("any")}
            </span>
            <X size={12} aria-hidden="true" />
          </button>
        ) : null}
        <button type="button" className="clear-link" onClick={onClearAll}>
          <span>{t("clearAll")}</span>
        </button>
      </div>
    </div>
  );
}
